// Copyright (C) 2026, Lifefinder Systems International AB

import React, { useEffect } from 'react';
import { Circle, Polygon, Popup } from 'react-leaflet';

import { useDevices } from '../../../context/DeviceContext';
import { useGeofenceAreas } from '../../../context/GeofenceAreaContext';
import { alarmColor, activeColor, traceColor } from '../../../styles/constants';
import type { Device, LatLng } from '../../../types';
import { showAsAlarm, showAsActive, showAsTrace } from '../../../utils/deviceUtils';

const FILL_OPACITY = 0.15;
const COLOR_EMPTY = '#4A90E2';
const COLOR_OCCUPIED = '#1a9';
const COLOR_ALARM = '#E00';

const PULSE_CLASS = 'geofence-area-pulse';
const STYLE_ID = 'geofence-area-styles';

function ensurePulseStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes ${PULSE_CLASS} {
      0%, 100% { fill-opacity: ${FILL_OPACITY}; }
      50%       { fill-opacity: 0.40; }
    }
    .${PULSE_CLASS} { animation: ${PULSE_CLASS} 1.5s ease-in-out infinite; }
  `;
  document.head.appendChild(style);
}

// Priority: alarm > trace > active > default gray.
const getDeviceStatusColor = (d: Device): string => {
  if (showAsAlarm(d)) return alarmColor;
  if (showAsTrace(d)) return traceColor;
  if (showAsActive(d)) return activeColor;
  return '#444';
};

export interface AreaStatus {
  color: string;
  pulse: boolean;
}

/**
 * Compute display color for an area based on devices currently inside it.
 *
 * Priority: alarm (red pulse) > occupied (green) > empty (blue).
 * Matching: device.area === area.geofence_area (set by solver when geofenced).
 */
export function getAreaStatus(
  area: { geofence_area: string | null },
  devices: Device[]
): AreaStatus {
  if (!area.geofence_area) return { color: COLOR_EMPTY, pulse: false };

  const inside = devices.filter((d) => d.area === area.geofence_area);
  if (inside.some((d) => showAsAlarm(d))) return { color: COLOR_ALARM, pulse: true };
  if (inside.length > 0) return { color: COLOR_OCCUPIED, pulse: false };
  return { color: COLOR_EMPTY, pulse: false };
}

export interface GeofenceAreasProps {
  /** Highlight this area id with a dashed stroke when the editor is open. */
  editingAreaId?: number | null;
}

/**
 * GeofenceAreas — Leaflet layer that renders transparent area overlays.
 *
 * Fetches area definitions from geofence-area-configs (the area is the source
 * of truth for shape and position since the multi-beacon refactor).
 */
const GeofenceAreas: React.FC<GeofenceAreasProps> = ({ editingAreaId }) => {
  const { devices } = useDevices();
  // Single source of truth — shared with the map's marker-suppression set so the overlay
  // and the point markers can never disagree, and the configs are fetched once on auth.
  const { areaConfigs: configs } = useGeofenceAreas();

  useEffect(() => {
    ensurePulseStyles();
  }, []);

  return (
    <>
      {configs.map((cfg) => {
        if (!cfg.area_shape) return null;

        const { color, pulse } = getAreaStatus({ geofence_area: cfg.area_name }, devices);
        const isEditing = editingAreaId === cfg.id;
        const pathOptions = {
          color,
          fillColor: color,
          fillOpacity: FILL_OPACITY,
          opacity: 0.5,
          weight: isEditing ? 3 : 2,
          dashArray: isEditing ? '8 4' : undefined,
          className: pulse ? PULSE_CLASS : undefined,
        };

        const devicesInside = devices.filter((d) => d.area === cfg.area_name);

        const popup = (
          <Popup>
            <strong style={{ fontSize: '1.1em' }}>{cfg.area_name}</strong>
            {devicesInside.length > 0 ? (
              <ul style={{ margin: '0.4em 0 0', paddingLeft: '1.2em', fontSize: '0.95em' }}>
                {devicesInside.map((d) => (
                  <li key={d.id} style={{ color: getDeviceStatusColor(d) }}>
                    {d.title || d.id}
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ color: '#888', marginTop: '0.3em', fontSize: '0.9em' }}>
                Inga enheter
              </div>
            )}
          </Popup>
        );

        if (cfg.area_shape.type === 'circle') {
          const [lat, lng] = cfg.area_shape.coords[0];
          return (
            <Circle
              key={cfg.id}
              center={[lat, lng] as LatLng}
              radius={cfg.area_shape.radius!}
              pathOptions={pathOptions}
            >
              {popup}
            </Circle>
          );
        }

        return (
          <Polygon
            key={cfg.id}
            positions={cfg.area_shape.coords as LatLng[]}
            pathOptions={pathOptions}
          >
            {popup}
          </Polygon>
        );
      })}
    </>
  );
};

export default GeofenceAreas;
